import { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import css from "../../../assets/css.png";
import js from "../../../assets/js.png";
import ts from "../../../assets/typescript.svg";
import sass from "../../../assets/sass.png";
import react from "../../../assets/react.png";
import tailwind from "../../../assets/tailwind.png";
import figma from "../../../assets/figma.png";
import redux from "../../../assets/redux.png";
import graphql from "../../../assets/graphql.png";
import nextjs from "../../../assets/next.png";
import git from "../../../assets/git.png";
import electron from "../../../assets/electron.png";
import postgress from "../../../assets/postgress.png";
import prisma from "../../../assets/prisma.webp";
import claude from "../../../assets/claude.png";
import cursor from "../../../assets/cursor.png";

gsap.registerPlugin(ScrollTrigger);

const skillGroups = [
    {
        title: "Frontend",
        titleRef: true,
        items: [
            { label: "JavaScript", icon: js },
            { label: "TypeScript", icon: ts, iconRounded: true },
            { label: "React", icon: react },
            { label: "Next.js", icon: nextjs, iconClass: "h-6 w-[1.35rem] object-contain" },
            { label: "Redux", icon: redux },
            { label: "Tailwind CSS", icon: tailwind },
            { label: "Figma", icon: figma },
        ],
    },
    {
        title: "AI / Integrations",
        items: [
            { label: "Electron", icon: electron },
            { label: "Claude Code", icon: claude },
            { label: "Cursor", icon: cursor },
        ],
    },
    {
        title: "Backend",
        items: [
            { label: "GraphQL", icon: graphql },
            { label: "Prisma", icon: prisma, iconWhiteBg: true },
            { label: "PostgreSQL", icon: postgress },
        ],
    },
];

function SkillChip({ label, icon, iconRounded, iconClass, iconWhiteBg }) {
    const imgClass =
        iconClass ??
        `h-6 w-6 shrink-0 object-contain ${iconRounded ? "rounded-full" : ""}`;

    return (
        <div className="inline-flex w-full items-center gap-2.5 rounded-md border border-N99/10 bg-N10/10 px-3 py-2.5 text-sm sm:text-base text-N80">
            {icon && (
                iconWhiteBg ? (
                    <div className="h-6 w-6 shrink-0 rounded-full bg-white/90 flex items-center justify-center">
                        <img src={icon} alt="" className="h-7 w-7 object-contain" />
                    </div>
                ) : (
                    <img src={icon} alt="" className={imgClass} />
                )
            )}
            <span className="font-medium tracking-wide text-N90">{label}</span>
        </div>
    );
}

const Skills = () => {
    const titleRef = useRef(null);
    const skillRef = useRef(null);
    const descendant = gsap.utils.selector(skillRef);

    useEffect(() => {
        gsap.fromTo(
            titleRef.current,
            { y: 0 },
            {
                y: -50,
                duration: 1,
                scrollTrigger: {
                    trigger: titleRef.current,
                },
            }
        );

        gsap.fromTo(
            descendant(".skill-card"),
            { y: 0, opacity: 0 },
            {
                y: -50,
                opacity: 1,
                duration: 1,
                stagger: {
                    amount: 1,
                    from: "start",
                },
                ease: "Power3.easeOut",
                scrollTrigger: {
                    trigger: descendant(".title-ref"),
                },
            }
        );
    }, [descendant]);

    return (
        <div id="skills" className="layout text-white">
            <h1
                ref={titleRef}
                className="relative mb-8 flex w-min items-center justify-center font-semibold text-5xl lg:mb-10 xl:text-6xl 2xl:text-7xl">
                <span>Skills</span>
            </h1>

            <div
                ref={skillRef}
                className="mb-32 grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-10 2xl:mb-48">
                {skillGroups.map((group) => (
                    <div
                        key={group.title}
                        className={`skill-card relative rounded-md border border-N99/5 bg-N5/80 p-6 opacity-0 duration-300 ease-in hover:bg-N5/40 sm:p-8 ${
                            group.titleRef ? "title-ref" : ""
                        }`}>
                        <h2 className="mb-3 text-lg font-semibold tracking-wide text-primary50 sm:mb-4 sm:text-xl">
                            {group.title}
                        </h2>
                        <div className="flex flex-col gap-2">
                            {group.items.map((item) => (
                                <SkillChip
                                    key={item.label}
                                    label={item.label}
                                    icon={item.icon}
                                    iconRounded={item.iconRounded}
                                    iconClass={item.iconClass}
                                    iconWhiteBg={item.iconWhiteBg}
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Skills;
